import {
    computed,
    defineComponent,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
} from "vue";

import ImageFallback from "../components/imageFallback.js";
import { DefaultEntityClient } from "../core/api/EntityClient.js";


const DefaultLogAccessResourceOptions = [
    {
        resource    : "logs.read",
        label       : "Lire les logs",
        description : "Lecture complète des logs du serveur sélectionné",
        category    : "logs",
        requires    : [],
        icon        : "article",
    },
    {
        resource    : "log_access.rules.manage",
        label       : "Gérer les règles",
        description : "Création, modification et suppression des règles d'accès",
        category    : "administration",
        requires    : [],
        icon        : "admin_panel_settings",
    },
];

const LogAccessResourceIcons = {
    "log_access.rules.manage"            : "admin_panel_settings",
    "logs.read"                          : "article",
    "logs.history.extended.read"         : "manage_history",
    "roles.read"                         : "groups",
    "channels.read"                      : "tag",
    "members.profile.read"               : "person",
    "members.profile.roles.read"         : "badge",
    "members.profile.activity.read"      : "timeline",
    "members.search.read"                : "person_search",
    "members.search.role_filter"         : "filter_alt",
    "voice.sessions.self.read"           : "person_pin_circle",
    "voice.sessions.read"                : "graphic_eq",
    "voice.interactions.read"            : "hub",
    "voice.interactions.channel.read"    : "settings_input_antenna",
    "voice.history.extended.read"        : "history",
    "sanctions.read"                     : "gavel",
    "sanctions.bans.read"                : "block",
    "sanctions.mutes.read"               : "volume_off",
    "sanctions.timeouts.read"            : "timer",
    "sanctions.reasons.read"             : "notes",
};

const ResourceCategoryLabels = {
    administration : "Administration",
    logs           : "Logs",
    members        : "Utilisateurs",
    metadata       : "Serveur",
    sanctions      : "Sanctions",
    voice          : "Vocal",
};

function normalizeLogAccessResourceOption(value) {
    const resource = typeof value?.resource === "string"
        ? value.resource
        : typeof value?.id === "string"
            ? value.id
            : "";

    return {
        resource,
        label       : typeof value?.label === "string" ? value.label : resource,
        description : typeof value?.description === "string" ? value.description : resource,
        category    : typeof value?.category === "string" ? value.category : "logs",
        requires    : Array.isArray(value?.requires) ? value.requires.filter((item) => typeof item === "string") : [],
        icon        : LogAccessResourceIcons[resource] ?? "key",
    };
}

function normalizeSearchValue(value) {
    return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLocaleLowerCase("fr");
}

function readRuleId(rule) {
    return rule?.ruleId || rule?.id || "";
}

function readMemberDisplayName(member) {
    return member?.serverNickname
        || member?.user?.globalName
        || member?.user?.username
        || member?.id
        || "Utilisateur inconnu";
}

function readMemberUsername(member) {
    return member?.user?.username
        ? `@${member.user.username}`
        : member?.id || "Compte introuvable";
}

function readMemberAvatar(member) {
    if (member?.serverAvatar || member?.user?.avatar)
        return member.serverAvatar || member.user.avatar;

    try {
        return `https://cdn.discordapp.com/embed/avatars/${(BigInt(member?.id || "0") >> 22n) % 6n}.png`;
    } catch {
        return "https://cdn.discordapp.com/embed/avatars/0.png";
    }
}

function readRoleColor(role) {
    return Number(role?.color) > 0
        ? `#${Number(role.color).toString(16).padStart(6, "0")}`
        : "#8f8f9d";
}

export default defineComponent({
    name: "LogAccessPolicies",

    setup() {
        const refRoles = ref([]);
        const refMembers = ref([]);
        const refLogAccessRules = ref([]);
        const refLogAccessResourceOptions = ref(DefaultLogAccessResourceOptions);
        const refLogAccessRulesLoading = ref(false);
        const refLogAccessRuleSaving = ref(false);
        const refLogAccessPolicyError = ref(null);
        const refLogAccessCanManage = ref(true);
        const refLogAccessRuleBuilderOpen = ref(false);
        const refEditingRuleId = ref(null);
        const refLogAccessRuleName = ref("");
        const refLogAccessDraftEnabled = ref(true);
        const refLogAccessDraftSubjects = ref([]);
        const refLogAccessDraftResources = ref([ "logs.read" ]);
        const refDraftSubjectKind = ref("role");
        const refDraftSubjectSearch = ref("");
        const refDraftSubjectFilter = ref("all");
        const refDraftResourceSearch = ref("");
        const refDraftResourceFilter = ref("all");
        const refDraftSubjectsOpen = ref(true);
        const refDraftResourcesOpen = ref(true);
        const refRuleSearch = ref("");
        const refRuleStatusFilter = ref("all");
        const refExpandedRuleIds = ref(new Set());

        const roleById = computed(() => new Map(refRoles.value.map((role) => [ role.id, role ])));
        const memberById = computed(() => new Map(refMembers.value.map((member) => [ member.id, member ])));
        const logAccessResourceOptionById = computed(() => new Map(refLogAccessResourceOptions.value.map((option) => [ option.resource, option ])));
        const selectedLogAccessDraftResources = computed(() => new Set(refLogAccessDraftResources.value));
        const selectedLogAccessDraftSubjects = computed(() => new Set(refLogAccessDraftSubjects.value.map(readSubjectKey)));
        let memberSearchController = null;
        let memberSearchTimer = null;

        const filteredDraftSubjectOptions = computed(() => {
            const type = refDraftSubjectKind.value === "user" ? "user" : "role";
            const selected = selectedLogAccessDraftSubjects.value;
            const search = normalizeSearchValue(refDraftSubjectSearch.value);
            const source = type === "role"
                ? refRoles.value.map((role) => ({
                    type,
                    id        : role.id,
                    role,
                    searchable: `${role.name} ${role.id}`,
                }))
                : refMembers.value.map((member) => ({
                    type,
                    id        : member.id,
                    member,
                    searchable: `${readMemberDisplayName(member)} ${readMemberUsername(member)} ${member.id}`,
                }));
            const knownIds = new Set(source.map((option) => option.id));

            for (const subject of refLogAccessDraftSubjects.value) {
                if (subject.type !== type || knownIds.has(subject.id))
                    continue;

                source.push({
                    type,
                    id        : subject.id,
                    searchable: subject.id,
                });
            }

            return source.filter((option) => {
                const isSelected = selected.has(readSubjectKey(option));
                const matchesState = refDraftSubjectFilter.value === "all"
                    || (refDraftSubjectFilter.value === "yes" && isSelected)
                    || (refDraftSubjectFilter.value === "no" && !isSelected);

                return matchesState && (!search || normalizeSearchValue(option.searchable).includes(search));
            });
        });

        const filteredDraftResourceOptions = computed(() => {
            const selected = selectedLogAccessDraftResources.value;
            const search = normalizeSearchValue(refDraftResourceSearch.value);

            return refLogAccessResourceOptions.value.filter((option) => {
                const isSelected = selected.has(option.resource);
                const matchesState = refDraftResourceFilter.value === "all"
                    || (refDraftResourceFilter.value === "yes" && isSelected)
                    || (refDraftResourceFilter.value === "no" && !isSelected);
                const searchable = normalizeSearchValue(`${option.label} ${option.description} ${option.category} ${option.resource}`);

                return matchesState && (!search || searchable.includes(search));
            });
        });

        const filteredLogAccessRules = computed(() => {
            const search = normalizeSearchValue(refRuleSearch.value);

            return refLogAccessRules.value.filter((rule) => {
                const enabled = rule.enabled !== false;
                const matchesState = refRuleStatusFilter.value === "all"
                    || (refRuleStatusFilter.value === "yes" && enabled)
                    || (refRuleStatusFilter.value === "no" && !enabled);
                const subjectNames = (rule.subjects || []).map(readLogAccessSubjectLabel).join(" ");
                const searchable = normalizeSearchValue(`${rule.name} ${subjectNames}`);

                return matchesState && (!search || searchable.includes(search));
            });
        });

        onMounted(loadLogAccessRules);

        watch(
            [ refDraftSubjectSearch, refDraftSubjectKind, refLogAccessRuleBuilderOpen ],
            ([ query, kind, builderOpen ]) => {
                if (memberSearchTimer !== null)
                    window.clearTimeout(memberSearchTimer);

                if (!builderOpen || kind !== "user")
                    return;

                memberSearchTimer = window.setTimeout(() => {
                    memberSearchTimer = null;
                    void loadMemberOptions(query);
                }, 250);
            },
        );

        onBeforeUnmount(() => {
            if (memberSearchTimer !== null)
                window.clearTimeout(memberSearchTimer);

            memberSearchController?.abort();
        });

        async function loadLogAccessRules() {
            refLogAccessRulesLoading.value = true;
            refLogAccessPolicyError.value = null;

            try {
                const response = await fetch("/api/log-access/rules");

                if (response.status === 403) {
                    refLogAccessPolicyError.value = "Tu n'as pas accès à la gestion des règles.";
                    refLogAccessCanManage.value = false;
                    refLogAccessRuleBuilderOpen.value = false;
                    refLogAccessRules.value = [];
                    return;
                }

                if (!response.ok) {
                    refLogAccessPolicyError.value = "Impossible de charger les règles d'accès.";
                    refLogAccessRules.value = [];
                    return;
                }

                const payload = await response.json();
                refLogAccessCanManage.value = true;

                if (Array.isArray(payload?.resources)) {
                    const resources = payload.resources
                        .map(normalizeLogAccessResourceOption)
                        .filter((option) => option.resource);

                    if (resources.length)
                        refLogAccessResourceOptions.value = resources;
                }

                refLogAccessRules.value = Array.isArray(payload?.rules) ? payload.rules : [];
                void loadPolicyEntities();

            } finally {
                refLogAccessRulesLoading.value = false;
            }
        }

        function mergeMembers(...memberGroups) {
            const merged = new Map(refMembers.value.map((member) => [ member.id, member ]));

            for (const members of memberGroups)
                for (const member of members)
                    if (member?.id)
                        merged.set(member.id, member);

            refMembers.value = [ ...merged.values() ].sort((left, right) => (
                readMemberDisplayName(left).localeCompare(readMemberDisplayName(right), "fr", { sensitivity: "base" })
            ));
        }

        async function loadPolicyEntities() {
            const selectedUserIds = [ ...new Set(refLogAccessRules.value
                .flatMap((rule) => rule.subjects || [])
                .filter((subject) => subject.type === "user")
                .map((subject) => subject.id)
            ) ];
            refRoles.value = [];
            refMembers.value = [];

            const [ roles, selectedMembers, memberSearch ] = await Promise.all([
                DefaultEntityClient.getRoles().catch(() => []),
                selectedUserIds.length
                    ? DefaultEntityClient.getMembers(selectedUserIds).catch(() => [])
                    : Promise.resolve([]),
                DefaultEntityClient.searchMembers({
                    query : "",
                    limit : 500,
                }).catch(() => ({
                    total   : 0,
                    members : [],
                })),
            ]);

            refRoles.value = roles.filter((role) => role.name !== "@everyone");
            mergeMembers(selectedMembers, memberSearch.members);
        }

        async function loadMemberOptions(query) {
            memberSearchController?.abort();
            memberSearchController = new AbortController();

            try {
                const response = await DefaultEntityClient.searchMembers({
                    query : String(query || "").trim(),
                    limit : 500,
                }, memberSearchController.signal);

                mergeMembers(response.members);

            } catch {
                // Missing ACLs and unknown users deliberately fall back to ID-only placeholders.
            }
        }

        function readSubjectKey(subject) {
            return `${subject.type}:${subject.id}`;
        }

        function resetLogAccessDraft() {
            refEditingRuleId.value = null;
            refLogAccessRuleName.value = "";
            refLogAccessDraftEnabled.value = true;
            refLogAccessDraftSubjects.value = [];
            refLogAccessDraftResources.value = [ "logs.read" ];
            refDraftSubjectKind.value = "role";
            refDraftSubjectSearch.value = "";
            refDraftSubjectFilter.value = "all";
            refDraftResourceSearch.value = "";
            refDraftResourceFilter.value = "all";
            refDraftSubjectsOpen.value = true;
            refDraftResourcesOpen.value = true;
        }

        async function scrollRuleBuilderIntoView() {
            await nextTick();
            document.querySelector(".settings-policy-editor")?.scrollIntoView({
                behavior : "smooth",
                block    : "start",
            });
        }

        function openLogAccessRuleBuilder() {
            resetLogAccessDraft();
            refLogAccessPolicyError.value = null;
            refLogAccessRuleBuilderOpen.value = true;
            scrollRuleBuilderIntoView();
        }

        function editLogAccessRule(rule) {
            refEditingRuleId.value = readRuleId(rule);
            refLogAccessRuleName.value = rule.name || "";
            refLogAccessDraftEnabled.value = rule.enabled !== false;
            refLogAccessDraftSubjects.value = (rule.subjects || []).map((subject) => ({ ...subject }));
            refLogAccessDraftResources.value = (rule.resources || []).map((resource) => resource.resource);
            refDraftSubjectKind.value = rule.subjects?.[0]?.type === "user" ? "user" : "role";
            refDraftSubjectSearch.value = "";
            refDraftSubjectFilter.value = "all";
            refDraftResourceSearch.value = "";
            refDraftResourceFilter.value = "all";
            refDraftSubjectsOpen.value = true;
            refDraftResourcesOpen.value = true;
            refLogAccessPolicyError.value = null;
            refLogAccessRuleBuilderOpen.value = true;
            scrollRuleBuilderIntoView();
        }

        function closeLogAccessRuleBuilder() {
            refLogAccessRuleBuilderOpen.value = false;
            resetLogAccessDraft();
        }

        function toggleLogAccessDraftSubject(subject) {
            const key = readSubjectKey(subject);

            if (selectedLogAccessDraftSubjects.value.has(key)) {
                refLogAccessDraftSubjects.value = refLogAccessDraftSubjects.value
                    .filter((item) => readSubjectKey(item) !== key);
                return;
            }

            refLogAccessDraftSubjects.value = [
                ...refLogAccessDraftSubjects.value,
                {
                    type : subject.type,
                    id   : subject.id,
                },
            ];
        }

        function addLogAccessDraftResourceWithRequirements(resource, target) {
            const option = logAccessResourceOptionById.value.get(resource);

            for (const requirement of option?.requires || [])
                addLogAccessDraftResourceWithRequirements(requirement, target);

            target.add(resource);
        }

        function toggleLogAccessDraftResource(resource) {
            if (selectedLogAccessDraftResources.value.has(resource)) {
                refLogAccessDraftResources.value = pruneLogAccessDraftResources(
                    refLogAccessDraftResources.value.filter((item) => item !== resource)
                );
                return;
            }

            const next = new Set(refLogAccessDraftResources.value);

            addLogAccessDraftResourceWithRequirements(resource, next);
            refLogAccessDraftResources.value = refLogAccessResourceOptions.value
                .filter((option) => next.has(option.resource))
                .map((option) => option.resource);
            refLogAccessPolicyError.value = null;
        }

        function pruneLogAccessDraftResources(resources) {
            let next = [ ...resources ];
            let changed = true;

            while (changed) {
                changed = false;
                const selected = new Set(next);

                next = next.filter((resource) => {
                    const option = logAccessResourceOptionById.value.get(resource);
                    const keep = (option?.requires || []).every((requirement) => selected.has(requirement));

                    if (!keep)
                        changed = true;

                    return keep;
                });
            }

            return next;
        }

        async function saveLogAccessRule() {
            const name = refLogAccessRuleName.value.trim();
            const resources = refLogAccessResourceOptions.value
                .filter((option) => refLogAccessDraftResources.value.includes(option.resource))
                .map((option) => ({
                    resource: option.resource,
                    context: {
                        scope: "guild",
                    },
                }));

            if (!name) {
                refLogAccessPolicyError.value = "Le nom de la règle est obligatoire.";
                return;
            }

            if (!refLogAccessDraftSubjects.value.length) {
                refLogAccessPolicyError.value = "Sélectionne au moins un utilisateur ou un rôle.";
                return;
            }

            if (!resources.length) {
                refLogAccessPolicyError.value = "Active au moins une permission.";
                return;
            }

            const editingRuleId = refEditingRuleId.value;
            refLogAccessRuleSaving.value = true;
            refLogAccessPolicyError.value = null;

            try {
                const response = await fetch(
                    editingRuleId
                        ? `/api/log-access/rules/${encodeURIComponent(editingRuleId)}`
                        : "/api/log-access/rules",
                    {
                        method: editingRuleId ? "PATCH" : "POST",
                        headers: {
                            "content-type": "application/json",
                        },
                        body: JSON.stringify({
                            name,
                            subjects: refLogAccessDraftSubjects.value,
                            resources,
                            enabled: refLogAccessDraftEnabled.value,
                        }),
                    },
                );

                if (!response.ok) {
                    const payload = await response.json().catch(() => null);
                    refLogAccessPolicyError.value = payload?.error || `Impossible ${editingRuleId ? "de modifier" : "de créer"} la règle.`;
                    return;
                }

                const payload = await response.json();

                if (payload?.rule) {
                    if (editingRuleId)
                        refLogAccessRules.value = refLogAccessRules.value.map((rule) => readRuleId(rule) === editingRuleId ? payload.rule : rule);
                    else
                        refLogAccessRules.value = [ payload.rule, ...refLogAccessRules.value ];

                    refExpandedRuleIds.value = new Set([
                        ...refExpandedRuleIds.value,
                        readRuleId(payload.rule),
                    ]);
                }

                closeLogAccessRuleBuilder();

            } finally {
                refLogAccessRuleSaving.value = false;
            }
        }

        async function deleteLogAccessRule(rule) {
            if (!window.confirm(`Supprimer la règle "${rule.name}" ?`))
                return;

            const ruleId = readRuleId(rule);
            const response = await fetch(`/api/log-access/rules/${encodeURIComponent(ruleId)}`, {
                method: "DELETE",
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => null);
                refLogAccessPolicyError.value = payload?.error || "Impossible de supprimer la règle.";
                return;
            }

            if (refEditingRuleId.value === ruleId)
                closeLogAccessRuleBuilder();

            refLogAccessRules.value = refLogAccessRules.value.filter((item) => readRuleId(item) !== ruleId);
        }

        function toggleExpandedRule(rule) {
            const ruleId = readRuleId(rule);
            const next = new Set(refExpandedRuleIds.value);

            if (next.has(ruleId))
                next.delete(ruleId);
            else
                next.add(ruleId);

            refExpandedRuleIds.value = next;
        }

        function readLogAccessSubjectLabel(subject) {
            if (subject.type === "role")
                return roleById.value.get(subject.id)?.name ?? `Rôle ${subject.id}`;

            const member = memberById.value.get(subject.id);

            return member
                ? `${readMemberDisplayName(member)} ${readMemberUsername(member)}`
                : `Utilisateur ${subject.id}`;
        }

        function readLogAccessResourceName(resource) {
            const resourceName = typeof resource === "string" ? resource : resource.resource;
            return logAccessResourceOptionById.value.get(resourceName)?.label ?? resourceName;
        }

        function readLogAccessResourceIcon(resource) {
            const resourceName = typeof resource === "string" ? resource : resource.resource;
            return logAccessResourceOptionById.value.get(resourceName)?.icon ?? "article";
        }

        function renderRoleIdentity(role, id) {
            const roleAvailable = Boolean(role);
            const resolvedRole = role || {
                id,
                name: "Rôle inconnu",
            };
            const icon = resolvedRole.iconURL || resolvedRole.icon;

            return (
                <router-link class="settings-policy-subject-link" to={`/roles/${id}`} title={`Ouvrir le rôle ${resolvedRole.name}`}>
                    <span class="settings-policy-subject-avatar settings-policy-role-avatar" style={{ "--subject-color": readRoleColor(resolvedRole) }}>
                        {icon
                            ? <img src={icon} alt="" />
                            : <i class="material-symbols-outlined">groups</i>}
                    </span>
                    <span class="settings-policy-subject-copy">
                        <strong>{resolvedRole.name}</strong>
                        <small>{roleAvailable ? "Rôle Discord" : id}</small>
                    </span>
                    <i class="material-symbols-outlined settings-policy-open-icon">open_in_new</i>
                </router-link>
            );
        }

        function renderMemberIdentity(member, id) {
            const displayName = member ? readMemberDisplayName(member) : `Utilisateur ${id}`;

            return (
                <router-link class="settings-policy-subject-link" to={`/users/${id}`} title={`Ouvrir le profil de ${displayName}`}>
                    <ImageFallback
                        class="settings-policy-subject-avatar"
                        src={readMemberAvatar(member || { id })}
                        default="https://cdn.discordapp.com/embed/avatars/0.png"
                        alt={displayName}
                    />
                    <span class="settings-policy-subject-copy">
                        <strong>{displayName}</strong>
                        <small>{member ? readMemberUsername(member) : id}</small>
                    </span>
                    <i class="material-symbols-outlined settings-policy-open-icon">open_in_new</i>
                </router-link>
            );
        }

        function renderSubjectIdentity(subject) {
            return subject.type === "role"
                ? renderRoleIdentity(roleById.value.get(subject.id), subject.id)
                : renderMemberIdentity(memberById.value.get(subject.id), subject.id);
        }

        function renderChoiceFilter(value, onSelect) {
            return (
                <div class="settings-policy-choice-filters" aria-label="Filtrer la sélection">
                    {[
                        [ "all", "Tous" ],
                        [ "yes", "Oui" ],
                        [ "no", "Non" ],
                    ].map(([ id, label ]) => (
                        <button
                            class={{ active: value === id }}
                            type="button"
                            aria-pressed={value === id}
                            onClick={() => onSelect(id)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            );
        }

        function renderDraftSubjectOption(option) {
            const selected = selectedLogAccessDraftSubjects.value.has(readSubjectKey(option));

            return (
                <div class={{ "settings-policy-choice": true, selected }}>
                    <button
                        class="settings-policy-choice-check"
                        type="button"
                        aria-label={`${selected ? "Retirer" : "Ajouter"} ${readLogAccessSubjectLabel(option)}`}
                        aria-pressed={selected}
                        onClick={() => toggleLogAccessDraftSubject(option)}
                    >
                        <span class="settings-policy-checkbox">
                            {selected && <i class="material-symbols-outlined">check</i>}
                        </span>
                    </button>

                    {option.type === "role"
                        ? renderRoleIdentity(option.role, option.id)
                        : renderMemberIdentity(option.member, option.id)}

                    <button
                        class={{ "settings-policy-choice-state": true, selected }}
                        type="button"
                        onClick={() => toggleLogAccessDraftSubject(option)}
                    >
                        {selected ? "Oui" : "Non"}
                    </button>
                </div>
            );
        }

        function renderDraftResourceOption(option) {
            const selected = selectedLogAccessDraftResources.value.has(option.resource);

            return (
                <button
                    class={{ "settings-policy-resource-choice": true, selected }}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleLogAccessDraftResource(option.resource)}
                >
                    <span class="settings-policy-checkbox">
                        {selected && <i class="material-symbols-outlined">check</i>}
                    </span>
                    <i class="material-symbols-outlined settings-policy-resource-icon">{option.icon}</i>
                    <span class="settings-policy-resource-copy">
                        <strong>{option.label}</strong>
                        <small>{option.description}</small>
                    </span>
                    <span class="settings-policy-resource-category">
                        {ResourceCategoryLabels[option.category] || option.category}
                    </span>
                    <span class={{ "settings-policy-choice-state": true, selected }}>
                        {selected ? "Oui" : "Non"}
                    </span>
                </button>
            );
        }

        function renderRuleBuilder() {
            if (!refLogAccessRuleBuilderOpen.value)
                return null;

            const editing = Boolean(refEditingRuleId.value);

            return (
                <div class="settings-policy-editor">
                    <div class="settings-policy-editor-header">
                        <div>
                            <span class="settings-eyebrow">{editing ? "Modification" : "Nouvelle règle"}</span>
                            <h3>{editing ? "Modifier la politique" : "Configurer les accès"}</h3>
                            <p>Sélectionne les utilisateurs, les rôles et chaque permission avec un choix explicite Oui ou Non.</p>
                        </div>

                        <button class="settings-policy-icon-button" type="button" title="Fermer" onClick={closeLogAccessRuleBuilder}>
                            <i class="material-symbols-outlined">close</i>
                        </button>
                    </div>

                    <div class="settings-policy-editor-basics">
                        <div class="settings-policy-field">
                            <label>Nom de la règle</label>
                            <input
                                maxlength="120"
                                placeholder="Ex. Équipe de modération"
                                value={refLogAccessRuleName.value}
                                onInput={(event) => refLogAccessRuleName.value = event.target.value}
                            />
                        </div>

                        <button
                            class={{ "settings-policy-enabled-toggle": true, active: refLogAccessDraftEnabled.value }}
                            type="button"
                            aria-pressed={refLogAccessDraftEnabled.value}
                            onClick={() => refLogAccessDraftEnabled.value = !refLogAccessDraftEnabled.value}
                        >
                            <span class="settings-policy-checkbox">
                                {refLogAccessDraftEnabled.value && <i class="material-symbols-outlined">check</i>}
                            </span>
                            <span>
                                <strong>Règle active</strong>
                                <small>{refLogAccessDraftEnabled.value ? "Les accès sont appliqués" : "La règle est conservée sans être appliquée"}</small>
                            </span>
                        </button>
                    </div>

                    <div class="settings-policy-editor-group">
                        <button class="settings-policy-editor-group-toggle" type="button" onClick={() => refDraftSubjectsOpen.value = !refDraftSubjectsOpen.value}>
                            <i class="material-symbols-outlined">group</i>
                            <span>
                                <strong>Utilisateurs et rôles</strong>
                                <small>{refLogAccessDraftSubjects.value.length} sélectionné{refLogAccessDraftSubjects.value.length > 1 ? "s" : ""}</small>
                            </span>
                            <i class="material-symbols-outlined">{refDraftSubjectsOpen.value ? "expand_less" : "expand_more"}</i>
                        </button>

                        {refDraftSubjectsOpen.value && (
                            <div class="settings-policy-editor-group-content">
                                <div class="settings-policy-kind-tabs">
                                    <button
                                        class={{ active: refDraftSubjectKind.value === "role" }}
                                        type="button"
                                        onClick={() => refDraftSubjectKind.value = "role"}
                                    >
                                        <i class="material-symbols-outlined">groups</i>
                                        Rôles
                                    </button>
                                    <button
                                        class={{ active: refDraftSubjectKind.value === "user" }}
                                        type="button"
                                        onClick={() => refDraftSubjectKind.value = "user"}
                                    >
                                        <i class="material-symbols-outlined">person</i>
                                        Utilisateurs
                                    </button>
                                </div>

                                <div class="settings-policy-list-toolbar">
                                    <label class="settings-policy-search">
                                        <i class="material-symbols-outlined">search</i>
                                        <input
                                            type="search"
                                            placeholder={`Rechercher ${refDraftSubjectKind.value === "role" ? "un rôle" : "un utilisateur"}`}
                                            value={refDraftSubjectSearch.value}
                                            onInput={(event) => refDraftSubjectSearch.value = event.target.value}
                                        />
                                    </label>
                                    {renderChoiceFilter(refDraftSubjectFilter.value, (value) => refDraftSubjectFilter.value = value)}
                                </div>

                                <div class="settings-policy-choice-list">
                                    {filteredDraftSubjectOptions.value.map(renderDraftSubjectOption)}
                                    {!filteredDraftSubjectOptions.value.length && (
                                        <div class="settings-policy-list-empty">Aucun résultat pour ces filtres.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div class="settings-policy-editor-group">
                        <button class="settings-policy-editor-group-toggle" type="button" onClick={() => refDraftResourcesOpen.value = !refDraftResourcesOpen.value}>
                            <i class="material-symbols-outlined">fact_check</i>
                            <span>
                                <strong>Permissions LogsCord</strong>
                                <small>{refLogAccessDraftResources.value.length} activée{refLogAccessDraftResources.value.length > 1 ? "s" : ""}</small>
                            </span>
                            <i class="material-symbols-outlined">{refDraftResourcesOpen.value ? "expand_less" : "expand_more"}</i>
                        </button>

                        {refDraftResourcesOpen.value && (
                            <div class="settings-policy-editor-group-content">
                                <div class="settings-policy-list-toolbar">
                                    <label class="settings-policy-search">
                                        <i class="material-symbols-outlined">search</i>
                                        <input
                                            type="search"
                                            placeholder="Rechercher une permission"
                                            value={refDraftResourceSearch.value}
                                            onInput={(event) => refDraftResourceSearch.value = event.target.value}
                                        />
                                    </label>
                                    {renderChoiceFilter(refDraftResourceFilter.value, (value) => refDraftResourceFilter.value = value)}
                                </div>

                                <div class="settings-policy-resource-choice-list">
                                    {filteredDraftResourceOptions.value.map(renderDraftResourceOption)}
                                    {!filteredDraftResourceOptions.value.length && (
                                        <div class="settings-policy-list-empty">Aucune permission pour ces filtres.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div class="settings-policy-builder-actions">
                        <button class="settings-button settings-button-secondary" type="button" onClick={closeLogAccessRuleBuilder}>
                            Annuler
                        </button>
                        <button class="settings-button settings-policy-create" disabled={refLogAccessRuleSaving.value} onClick={saveLogAccessRule}>
                            <i class="material-symbols-outlined">{editing ? "save" : "add"}</i>
                            {refLogAccessRuleSaving.value ? "Enregistrement..." : editing ? "Enregistrer les modifications" : "Créer la règle"}
                        </button>
                    </div>
                </div>
            );
        }

        function renderRule(rule) {
            const ruleId = readRuleId(rule);
            const expanded = refExpandedRuleIds.value.has(ruleId);
            const enabled = rule.enabled !== false;

            return (
                <article class={{ "settings-policy-rule-card": true, expanded }}>
                    <div class="settings-policy-rule-summary">
                        <button class="settings-policy-rule-expand" type="button" aria-expanded={expanded} onClick={() => toggleExpandedRule(rule)}>
                            <span class={{ "settings-policy-rule-icon": true, disabled: !enabled }}>
                                <i class="material-symbols-outlined">{enabled ? "verified_user" : "shield_lock"}</i>
                            </span>
                            <span class="settings-policy-rule-main">
                                <strong>{rule.name}</strong>
                                <small>
                                    {(rule.subjects || []).length} sujet{(rule.subjects || []).length > 1 ? "s" : ""}
                                    {" · "}
                                    {(rule.resources || []).length} permission{(rule.resources || []).length > 1 ? "s" : ""}
                                </small>
                            </span>
                            <span class={{ "settings-policy-rule-status": true, enabled }}>
                                <i class="material-symbols-outlined">{enabled ? "check_circle" : "pause_circle"}</i>
                                {enabled ? "Active" : "Inactive"}
                            </span>
                            <i class="material-symbols-outlined settings-policy-rule-chevron">{expanded ? "expand_less" : "expand_more"}</i>
                        </button>

                        <div class="settings-policy-rule-actions">
                            <button class="settings-button settings-button-secondary" type="button" onClick={() => editLogAccessRule(rule)}>
                                <i class="material-symbols-outlined">edit</i>
                                Modifier
                            </button>
                            <button class="settings-policy-icon-button settings-policy-delete" type="button" title="Supprimer la règle" onClick={() => deleteLogAccessRule(rule)}>
                                <i class="material-symbols-outlined">delete</i>
                            </button>
                        </div>
                    </div>

                    {expanded && (
                        <div class="settings-policy-rule-details">
                            <div>
                                <div class="settings-policy-rule-detail-title">
                                    <i class="material-symbols-outlined">group</i>
                                    <strong>Utilisateurs et rôles</strong>
                                </div>
                                <div class="settings-policy-rule-subjects">
                                    {(rule.subjects || []).map((subject) => renderSubjectIdentity(subject))}
                                </div>
                            </div>

                            <div>
                                <div class="settings-policy-rule-detail-title">
                                    <i class="material-symbols-outlined">fact_check</i>
                                    <strong>Permissions accordées</strong>
                                </div>
                                <div class="settings-policy-rule-resources">
                                    {(rule.resources || []).map((resource) => (
                                        <div class="settings-policy-rule-resource">
                                            <i class="material-symbols-outlined">{readLogAccessResourceIcon(resource)}</i>
                                            <span>
                                                <strong>{readLogAccessResourceName(resource)}</strong>
                                                <small>Autorisé</small>
                                            </span>
                                            <i class="material-symbols-outlined">check_circle</i>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </article>
            );
        }

        return () => (
            <div class="settings-page">
                <div class="settings-hero">
                    <span class="settings-eyebrow">Politiques</span>
                    <h1>Politiques d'accès</h1>
                    <p>Attribue précisément les ressources LogsCord à des utilisateurs ou à des rôles Discord.</p>

                    <div class="settings-policy-hero-actions">
                        <router-link class="settings-button settings-button-secondary" to="/settings">
                            <i class="material-symbols-outlined">arrow_back</i>
                            Paramètres
                        </router-link>
                    </div>
                </div>

                <div class="settings-policy-section">
                    <div class="settings-policy-header">
                        <div>
                            <span class="settings-eyebrow">Règles</span>
                            <h2>Accès aux ressources</h2>
                        </div>

                        <div class="settings-policy-header-actions">
                            <button class="settings-button settings-button-secondary" disabled={refLogAccessRulesLoading.value} onClick={loadLogAccessRules}>
                                <i class="material-symbols-outlined">refresh</i>
                                {refLogAccessRulesLoading.value ? "Chargement..." : "Actualiser"}
                            </button>
                            {refLogAccessCanManage.value && (
                                <button class="settings-button" type="button" disabled={refLogAccessRuleBuilderOpen.value} onClick={openLogAccessRuleBuilder}>
                                    <i class="material-symbols-outlined">add</i>
                                    Créer une règle
                                </button>
                            )}
                        </div>
                    </div>

                    {refLogAccessPolicyError.value && (
                        <div class="settings-policy-error">
                            <i class="material-symbols-outlined">error</i>
                            {refLogAccessPolicyError.value}
                        </div>
                    )}

                    {renderRuleBuilder()}

                    {refLogAccessCanManage.value && (
                        <>
                            <div class="settings-policy-rules-toolbar">
                                <label class="settings-policy-search">
                                    <i class="material-symbols-outlined">search</i>
                                    <input
                                        type="search"
                                        placeholder="Rechercher une règle ou un sujet"
                                        value={refRuleSearch.value}
                                        onInput={(event) => refRuleSearch.value = event.target.value}
                                    />
                                </label>
                                {renderChoiceFilter(refRuleStatusFilter.value, (value) => refRuleStatusFilter.value = value)}
                            </div>

                            <div class="settings-policy-rule-list">
                                {filteredLogAccessRules.value.map(renderRule)}

                                {!refLogAccessRulesLoading.value && !filteredLogAccessRules.value.length && (
                                    <div class="settings-policy-empty">
                                        <span>
                                            <i class="material-symbols-outlined">policy</i>
                                            {refLogAccessRules.value.length ? "Aucune règle ne correspond aux filtres." : "Aucune règle configurée."}
                                        </span>
                                        {!refLogAccessRules.value.length && !refLogAccessRuleBuilderOpen.value && (
                                            <button class="settings-button" type="button" onClick={openLogAccessRuleBuilder}>
                                                <i class="material-symbols-outlined">add</i>
                                                Créer une règle
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    },
});
